import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, UserCheck, Calendar, Star, ArrowRight, Shield, Clock, MapPin } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

const stepsClient = [
  { step: '01', icon: <Search size={28} />, color: '#FF6B00', title: 'Recherchez', desc: 'Entrez votre ville et le type de service dont vous avez besoin. Notre moteur trouve les prestataires disponibles près de vous.' },
  { step: '02', icon: <UserCheck size={28} />, color: '#10B981', title: 'Comparez', desc: 'Consultez les profils, les avis clients, les tarifs et les spécialités de chaque prestataire pour faire le bon choix.' },
  { step: '03', icon: <Calendar size={28} />, color: '#8B5CF6', title: 'Réservez', desc: 'Envoyez une demande de réservation directement depuis la plateforme. Le prestataire vous confirme sous quelques minutes.' },
  { step: '04', icon: <Star size={28} />, color: '#F59E0B', title: 'Évaluez', desc: 'Après l\'intervention, laissez un avis pour aider la communauté et récompenser les meilleurs prestataires.' },
];

const stepsPrestataire = [
  { step: '01', icon: '📝', color: '#FF6B00', title: 'Créez votre profil', desc: 'Inscrivez-vous gratuitement, renseignez vos services, votre zone d\'intervention et vos tarifs.' },
  { step: '02', icon: '✅', color: '#10B981', title: 'Faites-vous vérifier', desc: 'Notre équipe valide votre profil pour rassurer les clients. Le badge "Vérifié" booste votre visibilité.' },
  { step: '03', icon: '📲', color: '#8B5CF6', title: 'Recevez des demandes', desc: 'Les clients vous contactent directement. Acceptez ou refusez selon vos disponibilités.' },
  { step: '04', icon: '💰', color: '#F59E0B', title: 'Développez votre activité', desc: 'Cumulez les avis positifs et augmentez votre visibilité pour attirer encore plus de clients.' },
];

const avantages = [
  { icon: <Shield size={26} />, color: '#10B981', title: 'Prestataires vérifiés', desc: 'Chaque profil est contrôlé par notre équipe avant publication.' },
  { icon: <Clock size={26} />, color: '#FF6B00', title: 'Réponse rapide', desc: 'La plupart des prestataires répondent en moins de 30 minutes.' },
  { icon: <MapPin size={26} />, color: '#8B5CF6', title: 'Près de chez vous', desc: 'Trouvez un prestataire dans votre quartier ou votre ville.' },
  { icon: <Star size={26} />, color: '#F59E0B', title: 'Avis clients', desc: 'Des évaluations authentiques pour choisir en toute confiance.' },
];

export default function HowItWorksPage() {
  return (
    <div style={{ paddingTop: '80px', background: 'var(--gray-light)', minHeight: '100vh' }}>

      {/* HERO */}
      <div style={{
        background: 'linear-gradient(135deg, var(--blue-dark) 0%, #0D2137 100%)',
        padding: '5rem 0 4rem', textAlign: 'center',
      }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,107,0,0.15)',
              border: '1px solid rgba(255,107,0,0.3)',
              borderRadius: '50px', padding: '0.4rem 1.2rem',
              color: 'var(--orange)', fontSize: '0.8rem',
              fontFamily: 'var(--font-cond)', letterSpacing: '2px',
              textTransform: 'uppercase', marginBottom: '1.5rem',
            }}>
              Comment ça marche
            </div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              color: 'white', letterSpacing: '2px', marginBottom: '1rem',
            }}>
              SIMPLE, RAPIDE<br />
              <span style={{ color: 'var(--orange)' }}>ET EFFICACE</span>
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.1rem', maxWidth: 520, margin: '0 auto' }}>
              ELIM DEPANNAGE met en relation clients et mécaniciens en quelques clics.
            </p>
          </motion.div>
        </div>
      </div>

      {/* POUR LES CLIENTS */}
      <div className="container" style={{ padding: '5rem 1rem 3rem' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--blue-dark)', fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', letterSpacing: '2px', marginBottom: '0.5rem' }}>
            VOUS CHERCHEZ UN MÉCANICIEN ?
          </h2>
          <p style={{ color: 'var(--gray-dark)' }}>4 étapes pour trouver et réserver le bon prestataire</p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem' }}>
          {stepsClient.map((s, i) => (
            <motion.div
              key={s.step}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{
                background: 'white', borderRadius: '16px', padding: '2rem',
                boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                borderTop: `4px solid ${s.color}`,
                position: 'relative',
              }}
            >
              <div style={{
                position: 'absolute', top: '-1px', right: '1.5rem',
                background: s.color, color: 'white',
                fontFamily: 'var(--font-display)', fontSize: '0.75rem',
                padding: '0.2rem 0.7rem', borderRadius: '0 0 8px 8px', letterSpacing: '1px',
              }}>
                {s.step}
              </div>
              <div style={{
                width: 56, height: 56, borderRadius: '14px',
                background: s.color + '18', color: s.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '1.2rem',
              }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--blue-dark)', fontSize: '1.1rem', letterSpacing: '1px', marginBottom: '0.6rem' }}>
                {s.title}
              </h3>
              <p style={{ color: 'var(--gray-dark)', fontSize: '0.9rem', lineHeight: 1.7 }}>{s.desc}</p>
            </motion.div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <Link to="/prestataires" className="btn btn-primary btn-lg">
            Trouver un prestataire <ArrowRight size={18} style={{ marginLeft: 8 }} />
          </Link>
        </div>
      </div>

      {/* POUR LES PRESTATAIRES */}
      <div style={{ background: 'var(--blue-dark)', padding: '5rem 0' }}>
        <div className="container" style={{ padding: '0 1rem' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', color: 'white', fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', letterSpacing: '2px', marginBottom: '0.5rem' }}>
              VOUS ÊTES MÉCANICIEN ?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)' }}>Rejoignez la plateforme et développez votre clientèle</p>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem' }}>
            {stepsPrestataire.map((s, i) => (
              <motion.div
                key={s.step}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '16px', padding: '2rem',
                  borderTop: `4px solid ${s.color}`,
                  position: 'relative',
                }}
              >
                <div style={{
                  position: 'absolute', top: '-1px', right: '1.5rem',
                  background: s.color, color: 'white',
                  fontFamily: 'var(--font-display)', fontSize: '0.75rem',
                  padding: '0.2rem 0.7rem', borderRadius: '0 0 8px 8px', letterSpacing: '1px',
                }}>
                  {s.step}
                </div>
                <div style={{ fontSize: '2.2rem', marginBottom: '1.2rem' }}>{s.icon}</div>
                <h3 style={{ fontFamily: 'var(--font-display)', color: 'white', fontSize: '1.1rem', letterSpacing: '1px', marginBottom: '0.6rem' }}>
                  {s.title}
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: 1.7 }}>{s.desc}</p>
              </motion.div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/inscription" className="btn btn-primary btn-lg">
              S'inscrire gratuitement <ArrowRight size={18} style={{ marginLeft: 8 }} />
            </Link>
          </div>
        </div>
      </div>

      {/* AVANTAGES */}
      <div className="container" style={{ padding: '5rem 1rem' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--blue-dark)', fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', letterSpacing: '2px' }}>
            POURQUOI NOUS CHOISIR ?
          </h2>
        </motion.div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
          {avantages.map((a, i) => (
            <motion.div
              key={a.title}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{
                background: 'white', borderRadius: '14px', padding: '1.8rem',
                boxShadow: '0 4px 20px rgba(0,0,0,0.07)', textAlign: 'center',
              }}
            >
              <div style={{
                width: 56, height: 56, borderRadius: '14px',
                background: a.color + '18', color: a.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1rem',
              }}>
                {a.icon}
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', color: 'var(--blue-dark)', fontSize: '1rem', letterSpacing: '1px', marginBottom: '0.5rem' }}>
                {a.title}
              </h3>
              <p style={{ color: 'var(--gray-dark)', fontSize: '0.88rem', lineHeight: 1.6 }}>{a.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
